import { DataCardProps } from "@/components-v2/DataCard";
import ShareholdersStore, {
  ShareholderDetailsState,
  ShareholderDetailsStore,
} from "@/stores/ShareholdersStore";
import {
  ExtendedShareholderDTO,
  getGenderLabel,
  getShareTypeVote,
  PersonInfo,
  ShareholderDetailsResponseDTO,
  ShareholdingInformationPerShareClass,
  ShareInformation,
  ShareIntervalInformation,
  SummaryValue,
  totalNumberOfSharesOfShareholders,
} from "@/types/Shareholder";
import { getFullName } from "@/utils/Roles";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { compact, isEmpty, last } from "lodash";
import { formatOrgNum } from "@/utils/other";
import { DataTableProps } from "@/components-v2/DataTable";
import { toDate } from "@/utils/Dates";
import { Transaction } from "@/types/Transactions";
import {
  prepareTransactionLogTableColumns,
  prepareTransactionLogTableRowActions,
} from "@/shareholderRegister/transactions/transactionLogDomain";
import { LocaleFormat } from "@/i18n/localeFormat";
import { formatPhoneNumber } from "@/utils/phoneNumbers";
import { isCountryCode } from "@/utils/countries";
import { when } from "@/utils/functions";
import { isOk, Result, ShareValuationInformation } from "@/common/common";
import {
  DetailsCardProps,
  ShareholderCommitmentsViewProps,
  ShareholderContributionsViewProps,
  ShareholderDetailsHeaderProps,
  ShareholdingSectionCard,
  ShareholdingSectionProps,
} from "./shareholderDetailsComponents";
import { next, prev } from "@/utils/arrays";
import { isSwedishOrgNo } from "@/utils/orgNo";
import { adminSettingsShareholders, navigationState } from "@/utils/navigation";
import { confirmAction, EventDispatcher, navigate, thunk } from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import {
  prepShareholderStatusFilter,
  ShareholderCommitment,
  ShareholderContribution,
  ShareholderPledgeForm,
  ShareholderRemarksForm,
  ShareholderStatus,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { action, flowResult } from "mobx";
import { LocalDateFieldModel } from "@/common/commonForms";
import {
  prepareCommitmentOverviewRows,
  prepareCommitmentOverviewTotal,
  prepareCommitmentRows,
  prepareShareholderCommitmentsEmptyState,
  prepareShareholderCommitmentsTable,
  ShareholderCommitmentController,
} from "@/shareholderRegister/commitments/shareholderCommitmentsDomain";
import { isShareholderDetailsTab, ShareholderDetailsTab } from "./shareholderDetailsTypes";
import { combineExtents, extent } from "@/components-v2/sparklineHelpers";
import { lookupShareTypeName, ShareType } from "@/common/share";
import { AlertProps } from "@/components-v2/Alert";
import { TMessageKey } from "@/i18n/types.gen";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { InfoTagProps } from "@/components-v2/InfoTag";
import { InvitationType } from "@/types/Invitation";
import { SendInvitationDialogStore } from "@/stores/SendInvitationDialogStore";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { ApiService } from "@/api";
import { AIClient } from "@/ai/AIAssistant";
import { AxiosInstance } from "axios";
import { User } from "@/types/UserInfo";
import { CustomRoleForUser } from "@/common/customRole";
import {
  prepareContributionOverviewTotal,
  prepareContributionRows,
  prepareContributionsEmptyState,
  prepareContributionsTable,
  prepareOverviewRows,
  ShareholderContributionController,
} from "@/shareholderRegister/contributions/shareholderContributionsDomain";

export type ShareholderDetailsActions = {
  edit?: () => void;
  editPledge?: () => void;
  editRemarks?: (remark?: string) => void;
  downloadShareholderCertificate?: () => void;
  sendInvite?: () => void;
  openTab?: (tab: ShareholderDetailsTab) => void;
  openTransaction?: (id: number) => void;
  downloadContractNote?: (transaction: Transaction) => void;
  openInviteRepresentativeDialog?: () => void;
  deleteRepresentative?: (id: number) => void;
  selectShareholder?: (s: { id: number; name: string }) => void;
  setShareholderStatusFilter?: (s: ShareholderStatus) => void;
  close?: () => void;
  navigateToCommitments?: () => void;
  navigateToCommitmentDetails?: (id: string) => void;
  deleteCommitment?: (id: string) => void;
  changeCommitmentDate?: (id: string) => void;
  deleteShareholder?: (confirmText: TMessage) => void;
  navigateToContributions?: () => void;
  navigateToContributionDetails?: (id: number) => void;
  showChangeDateDialogForContribution?: (contributionId: number) => void;
  showDeleteConfirmationDialogForContribution?: (contributionId: number) => void;
};

export type ShareholderDetailsContext = {
  orgNo: string;
  userRole: CustomRoleForUser;
  companyName?: string;
  shareholderId: number;
  companies?: { orgNo: string; name: string; shareholderId: number }[];
  activeTab?: ShareholderDetailsTab;
  commitmentsEnabled?: boolean;
  commitments?: Result<ShareholderCommitment[]>;
  contributionsEnabled?: boolean;
  contributions?: Result<ShareholderContribution[]>;
  optionsTabEnabled?: boolean;
  optionHolderSince?: string;
  // Overrides the header selector's population, e.g. option holders when the
  // user navigated here from the options register.
  shareholderSelectorItems?: { id: number; name: string }[];
  canEdit?: boolean;
  isShareholderView?: boolean;
  perDateFilter?: LocalDateFieldModel;
  t: TFunction;
  f: LocaleFormat;
  lang: Language;
};

const showCostPrice = (state: { shareholder?: ShareholderDetailsResponseDTO }): boolean =>
  !state.shareholder?.hasIncompleteCostPriceHistory ||
  Boolean(state.shareholder?.overrideCostPriceDisplay);

export const toShareholderDetailsTab = (s: string | undefined): ShareholderDetailsTab =>
  isShareholderDetailsTab(s) ? s : "overview";

// Option holders that hold no shares (and never did) get a trimmed-down view:
// only the options and details tabs apply to them.
export const isOptionHolderWithoutShares = (
  context: { optionsTabEnabled?: boolean },
  state: { shareholder?: ShareholderDetailsResponseDTO },
): boolean =>
  Boolean(context.optionsTabEnabled) && state.shareholder !== undefined && isNotShareholder(state);

const getTabs = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
): [id: ShareholderDetailsTab, label: TMessage][] =>
  isOptionHolderWithoutShares(context, state)
    ? [
        [
          "options",
          {
            key: "shareholder-details.options-in",
            params: { companyName: context.companyName },
          },
        ],
        ["details", { key: "shareholders.details" }],
      ]
    : compact([
        [
          "overview",
          {
            key: "shareholder-details.shareholding-in",
            params: { companyName: context.companyName },
          },
        ],
        context.userRole.allowTransactionsView && [
          "transactions",
          { key: "shareholder-details.transactions" },
        ],
        ["details", { key: "shareholders.details" }],
        context.commitmentsEnabled && [
          "commitments",
          {
            key: "shareholder-commitments.commitments.heading",
          },
        ],
        context.contributionsEnabled && [
          "contributions",
          { key: "shareholder-contributions.contributions.heading" },
        ],
        context.optionsTabEnabled && ["options", { key: "header.options" }],
        [
          "additional-representatives",
          {
            key: "additional-representatives-n",
            params: { count: state.shareholder?.additionalRepresentatives?.length ?? 0 },
          },
        ],
      ]);

const getShareholdingPerClass = (
  state: ShareholderDetailsState,
): ShareholdingInformationPerShareClass[] =>
  state.shareholder?.shareInformation?.shareholding ?? [];

const getTotalNumberOfShares = (state: { shareholder?: ShareholderDetailsResponseDTO }): number =>
  state.shareholder?.shareInformation?.totalShares ?? 0;

const getFirstTransactionDate = (state: {
  shareholder?: ShareholderDetailsResponseDTO;
}): string | undefined => state.shareholder?.shareInformation?.firstTransactionDate;

const isFormerShareholder = (state: { shareholder?: ShareholderDetailsResponseDTO }): boolean =>
  getTotalNumberOfShares(state) === 0 && getFirstTransactionDate(state) !== undefined;

const isNotShareholder = (state: { shareholder?: ShareholderDetailsResponseDTO }): boolean =>
  getTotalNumberOfShares(state) === 0 && getFirstTransactionDate(state) === undefined;

export const prepShareholderInCompanyText = (
  state: { shareholder?: ShareholderDetailsResponseDTO },
  context: {
    companyName?: string;
    optionHolderSince?: string;
  },
): TMessage | undefined =>
  !state.shareholder
    ? undefined
    : isFormerShareholder(state)
      ? {
          key: "shareholder-details.former-shareholder-in",
          params: { companyName: context.companyName },
        }
      : isNotShareholder(state)
        ? context.optionHolderSince !== undefined
          ? {
              key: "shareholder-details.option-holder-in",
              params: {
                companyName: context.companyName,
                date: { type: "date", value: context.optionHolderSince },
              },
            }
          : {
              key: "shareholder-details.not-shareholder-in",
              params: { companyName: context.companyName },
            }
        : {
            key: "shareholder-details.shareholder-in",
            params: {
              companyName: context.companyName,
              date: when(getFirstTransactionDate(state), (date) => ({
                type: "date",
                value: date,
              })),
            },
          };

export const prepShareholderInfoTags = (shareholder?: ExtendedShareholderDTO): InfoTagProps[] => {
  const { birthDateOrOrganisationNumber, contactInfo, isCompany, nationality } = shareholder ?? {};

  return compact([
    nationality && {
      id: "nationality",
      text: { translated: nationality },
      tooltip: isCompany
        ? {
            key: "shareholder-details.country",
            params: { code: { type: "country", code: nationality } },
          }
        : {
            key: "shareholder-details.nationality",
            params: { code: { type: "country", code: nationality } },
          },
    },
    birthDateOrOrganisationNumber && {
      id: "birthDateOrOrganisationNumber",
      icon: isCompany ? "buildings" : "user-square",
      text: isCompany
        ? {
            translated: formatOrgNum(birthDateOrOrganisationNumber),
          }
        : { type: "date", value: birthDateOrOrganisationNumber },
      tooltip: {
        key: isCompany
          ? "shareholder-details.company-shareholder"
          : "shareholder-details.personal-shareholder",
      },
    },
    isCompany &&
      contactInfo && {
        id: "contact",
        icon: "user-square",
        text: { translated: getFullName(contactInfo) },
        tooltip: { key: "shareholder-details.primary-representative" },
      },
  ]);
};

// The viewed shareholder can be missing from the dropdown's population (e.g. a
// zero-share option holder is excluded by the "current" status filter); the
// select would reset to null otherwise, so the current entry goes on top.
export const withCurrentShareholder = (
  items: { id: number; name: string }[],
  current: { id: number; name: string },
): { id: number; name: string }[] =>
  items.some((s) => s.id === current.id) ? items : [current, ...items];

export const prepShareholderDetailsHeader = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): ShareholderDetailsHeaderProps => {
  const shareholder = state.allShareholders.find((s) => s.id === context.shareholderId);
  // The list may not contain freshly created shareholders (e.g. option holders
  // registered moments ago) — the loaded details still know the name.
  const name = shareholder?.name ?? state.shareholder?.shareholderName ?? "";
  const selectorItems = withCurrentShareholder(
    context.shareholderSelectorItems ?? state.filteredShareholders,
    { id: context.shareholderId, name },
  );
  const lastLogin = shareholder?.lastLogin;

  return {
    shareholderName: name || undefined,
    companySelector: context.companies
      ? {
          items: context.companies,
          value: context.companies.find((c) => c.shareholderId === context.shareholderId),
          onSelect: (c) => actions.selectShareholder?.({ id: c.shareholderId, name }),
          idFn: (c) => c.orgNo,
          labelFn: (c) => ({ translated: c.name }),
          selectNext: when(
            next(context.companies ?? [], (c) => c.shareholderId === context.shareholderId),
            (c) => () => actions.selectShareholder?.({ id: c.shareholderId, name }),
          ),
          selectPrev: when(
            prev(context.companies ?? [], (c) => c.shareholderId === context.shareholderId),
            (c) => () => actions.selectShareholder?.({ id: c.shareholderId, name }),
          ),
        }
      : undefined,
    companyText: prepShareholderInCompanyText(state, context),
    onClose: actions.close,
    infoTags: prepShareholderInfoTags(shareholder),
    statusText: lastLogin
      ? {
          key: "last-login-with-date",
          params: { date: { type: "date", value: lastLogin, relative: true } },
        }
      : { key: "never-logged-in" },
    statusAction:
      !lastLogin && actions.sendInvite
        ? {
            icon: "envelope",
            label: { key: "shareholders.detailed.send-login-link" },
            onClick: actions.sendInvite,
          }
        : undefined,
    tabs: getTabs(context, state).map(([id, label], index) => ({
      id,
      label,
      isActive: context.activeTab === id || (!context.activeTab && index === 0),
      onSelect: () => actions.openTab?.(id),
    })),
    actions: compact([
      actions.edit && {
        type: "button",
        key: "edit-details",
        props: {
          variant: "secondary",
          icon: "edit",
          label: { key: "shareholder-details.edit-shareholder-details" },
          onClick: actions.edit,
        },
      },
      actions.downloadShareholderCertificate && {
        type: "button",
        key: "download-shareholder-certificate",
        props: {
          variant: "secondary",
          icon: "download",
          label: { key: "shareholder-certificate" },
          onClick: actions.downloadShareholderCertificate,
          loading: state.isDownloadingCertificate,
        },
      },
    ]),
    shareholderSelector: actions.selectShareholder
      ? {
          items: selectorItems,
          value: { id: context.shareholderId, name },
          idFn: (s) => String(s.id),
          labelFn: (s) => ({ translated: s.name }),
          // The select resets to null when its value vanishes from the items —
          // never a selection to act on.
          onSelect: (s: { id: number; name: string } | null) => {
            if (s !== null) {
              actions.selectShareholder?.(s);
            }
          },
          selectNext: when(
            next(selectorItems, (s) => s.id === context.shareholderId),
            (s) => () => actions.selectShareholder?.(s),
          ),
          selectPrev: when(
            prev(selectorItems, (s) => s.id === context.shareholderId),
            (s) => () => actions.selectShareholder?.(s),
          ),
        }
      : undefined,
    // The status filter narrows the selector's shareholder population — it does
    // not apply when that population is overridden (e.g. option holders).
    shareholderStatusFilter:
      actions.setShareholderStatusFilter &&
      !context.perDateFilter &&
      !context.shareholderSelectorItems
        ? prepShareholderStatusFilter(
            state.shareholderStatus,
            actions.setShareholderStatusFilter,
            state.allShareholders,
          )
        : undefined,
    shareholderPerDateFilter: context.perDateFilter,
  };
};

const shareIntervalValue = (s: ShareIntervalInformation): string => String(s.id);

const shareIntervalLabel = (s: ShareIntervalInformation): string | TMessageParam => ({
  type: "list",
  value: [
    { type: "number", value: s.from },
    { type: "number", value: s.to },
  ],
  separator: " - ",
});

const shareIntervalTickLabel = (_s: ShareIntervalInformation, index: number): string =>
  String(index + 1);

export const prepShareIntervalsTable = (
  s: ShareholdingInformationPerShareClass,
  showCostPrice: boolean,
): DataTableProps<ShareIntervalInformation> => {
  const showPaidInShareCapital = s.shareIntervals.some(
    (r) => r.paidInShareCapitalPerShare !== undefined && r.paidInShareCapitalPerShare !== 0,
  );
  const showBonusShareCapital = s.shareIntervals.some(
    (r) => r.bonusShareCapitalPerShare !== undefined && r.bonusShareCapitalPerShare !== 0,
  );
  return {
    tableData: s.shareIntervals,
    getId: (row) => row.id,
    withFooterRow: true,
    hoverable: true,
    tableColumns: compact([
      {
        name: "share-index",
        label: "",
        getValue: (_row, index) => `${index + 1}.`,
        getSortValue: (_row, index) => index,
        align: "left",
      },
      {
        name: "share-no",
        label: { key: "shareholder-details.share-no" },
        getValue: shareIntervalLabel,
        getFooterValue: () => ({ key: "total" }),
        getSortValue: (row) => row.from,
        align: "left",
      },
      {
        name: "number-of-shares",
        label: { key: "shareholder-details.number-of-shares" },
        getValue: (row) => ({ type: "number", value: row.numberOfShares }),
        getFooterValue: () => ({ type: "number", value: s.numberOfShares }),
        getSortValue: (row) => row.numberOfShares,
      },
      showCostPrice && {
        name: "cost-price-per-share",
        label: { key: "shareholder-details.cost-price-per-share" },
        getValue: (row) =>
          row.costPricePerShare !== undefined
            ? { type: "number", value: row.costPricePerShare }
            : undefined,
        getSortValue: (row) => row.costPricePerShare,
      },
      showCostPrice && {
        name: "aggr-cost-price",
        label: { key: "shareholder-details.aggr-cost-price" },
        getValue: (row) =>
          row.costPrice
            ? { type: "number", value: row.costPrice, maximumFractionDigits: 2 }
            : undefined,
        getFooterValue: () =>
          s.totalSharePrice
            ? {
                type: "number",
                value: s.totalSharePrice,
                maximumFractionDigits: 2,
              }
            : "",
        getSortValue: (row) => row.costPrice,
      },
      showPaidInShareCapital && {
        name: "paid-in-share-capital",
        label: { key: "shareholder-details.paid-in-share-capital-per-share" },
        getValue: (row) =>
          row.paidInShareCapitalPerShare !== undefined
            ? { type: "number", value: row.paidInShareCapitalPerShare }
            : undefined,
        getSortValue: (row) => row.paidInShareCapitalPerShare,
      },
      {
        name: `share-premium`,
        label: { key: "shareholder-details.share-premium-per-share" },
        getValue: (row) =>
          row.sharePremium !== undefined ? { type: "number", value: row.sharePremium } : undefined,
        getSortValue: (row) => row.sharePremium,
      },
      showBonusShareCapital && {
        name: "bonus-share-capital",
        label: { key: "shareholder-details.bonus-share-capital-per-share" },
        getValue: (row) =>
          row.bonusShareCapitalPerShare !== undefined
            ? { type: "number", value: row.bonusShareCapitalPerShare }
            : undefined,
        getSortValue: (row) => row.bonusShareCapitalPerShare,
      },
      {
        name: "transaction-date",
        label: { key: "transactions.transaction-date" },
        getValue: (row) => ({ type: "date", value: row.transactionDate }),
        getSortValue: (row) => toDate(row.transactionDate).getTime(),
      },
    ]),
  };
};

const perShareTypeLabel = (shareType: ShareType): TMessage => ({
  key: "shareholder-details.per-share",
  params: { shareType: { type: "shareType", value: shareType, options: { singular: true } } },
});

export const prepShareIntervalKpiCard = ({
  data,
  title,
  helpText,
  val,
  formatVal,
  domain,
  shareTypes,
  language,
}: {
  data: ShareholdingInformationPerShareClass;
  title: TMessage;
  helpText?: TMessage;
  val: (d: ShareIntervalInformation) => number | undefined;
  formatVal: (d: ShareIntervalInformation) => TMessageParam | string;
  domain: [number, number];
  shareTypes: ShareType[];
  language: Language;
}): DataCardProps<ShareIntervalInformation> => ({
  title,
  subtitle: { translated: lookupShareTypeName(data.shareTypeId, shareTypes, language) },
  infoPopover: helpText ? { content: helpText } : undefined,
  value: data.shareIntervals.length === 1 ? formatVal(data.shareIntervals[0]) : undefined,
  chart:
    data.shareIntervals.length <= 1
      ? undefined
      : {
          type: "sparkline",
          sparkline: {
            data: data.shareIntervals,
            components: [{ type: "bars" }, { type: "x-axis" }, { type: "value" }],
            x: {
              scaleType: "band",
              val: shareIntervalValue,
              formatVal: shareIntervalLabel,
              formatTickVal: shareIntervalTickLabel,
            },
            y: {
              scaleType: "linear",
              val,
              formatVal,
              domain,
            },
          },
        },
  extraInfo:
    data.shareIntervals.length === 1
      ? {
          key: "shareholder-details.share-no-n",
          params: { n: shareIntervalLabel(data.shareIntervals[0]) },
        }
      : {
          key: "shareholder-details.across-n-series-of-shares",
          params: { n: data.shareIntervals.length },
        },
});

export const prepShareValuationTable = (
  data: ShareValuationInformation[],
): DataTableProps<ShareValuationInformation> => ({
  tableData: data,
  tableColumns: [
    {
      name: "date",
      getValue: (row) => ({ type: "date", value: row.date }),
      getSortValue: (row) => toDate(row.date).getTime(),
      label: { key: "date" },
      align: "left",
    },
    {
      name: "value",
      getValue: (row) => ({ type: "amount", ...row.value }),
      getSortValue: (row) => row.value.value,
      label: { key: "value-per-share" },
    },
    {
      name: "numberOfShares",
      getValue: (row) => ({ type: "number", value: row.numberOfShares }),
      getSortValue: (row) => row.numberOfShares,
      label: { key: "shareholder-details.number-of-shares" },
    },
    {
      name: "totalValue",
      getValue: (row) => ({ type: "amount", ...row.totalValue }),
      getSortValue: (row) => row.totalValue.value,
      label: { key: "total-value" },
    },
  ],
  getId: (row) => row.date,
  variant: "IN_CARD_DENSE",
});

export const prepFairValueCards = (
  shareType: ShareType | undefined,
  fairValueInformation: ShareValuationInformation[] | undefined,
): ShareholdingSectionCard[] | undefined => {
  const xs = fairValueInformation?.filter((x) => x.shareTypeCode === shareType?.shareTypeCode);
  if (!xs || !xs.length || !shareType) {
    return;
  }
  const lastVal = last(xs)!;
  return [
    {
      title: { key: "fairValue" },
      subtitle: perShareTypeLabel(shareType),
      infoPopover: { content: { key: "shareholder-details.fair-value.help-text" } },
      value: { type: "amount", ...lastVal.value },
      chart:
        xs.length > 1
          ? {
              type: "sparkline",
              sparkline: {
                data: xs,
                x: { scaleType: "linear", val: (_: ShareValuationInformation, i: number) => i },
                y: { scaleType: "linear", val: (d: ShareValuationInformation) => d.value.value },
                components: [{ type: "line" }, { type: "dots" }, { type: "hover-value" }],
              },
            }
          : undefined,
      extraInfo: { key: "per-date", params: { date: { type: "date", value: lastVal.date } } },
    },
    {
      colSpan: 3,
      title: { key: "fairValueDetails" },
      table: prepShareValuationTable(xs),
    },
  ];
};

export const prepTaxAssessmentOverviewCard = (state: {
  shareholder?: ShareholderDetailsResponseDTO;
  currency: string;
}): DataCardProps<ShareValuationInformation> | undefined => {
  return when(last(state.shareholder?.taxAssessmentInformation), (t) => ({
    title: { key: "settings.taxAssessmentValue" },
    subtitle: {
      type: "list",
      value: [
        t.value.currency,
        { key: "per-date", params: { date: { type: "date", value: t.date } } },
      ],
      separator: " ",
    },
    value: { type: "number", value: t.value.value },
    extraInfo: { key: "shareholder-details.tax-assessment-help-text" },
    infoPopover: {
      iconColor: "info",
      icon: "clock-counter-clockwise",
      content: [
        "table",
        prepShareValuationTable(state.shareholder?.taxAssessmentInformation ?? []),
      ],
    },
  }));
};

const prepTotalSharesCard = (
  shareInformation: ShareInformation | undefined,
): ShareholdingSectionCard | undefined => {
  if (!shareInformation || shareInformation.shareholding.length <= 1) {
    return undefined;
  }
  return {
    title: { key: "shareholder-details.total-number-of-shares" },
    subtitle: { key: "shareholder-details.number-of-shares" },
    value: {
      type: "number",
      value: shareInformation.totalShares ?? 0,
    },
    extraInfo: {
      key: "x-of-total-xs",
      params: {
        x: { type: "percent", value: shareInformation.totalOwnership ?? 0 },
        xs: {
          type: "number",
          value: shareInformation.totalNumberOfSharesInCompany ?? 0,
        },
        unit: { type: "uncapitilize", value: { key: "shares" } },
      },
    },
  };
};

const prepShareClassOverviewCard = (
  state: { shareTypes: ShareType[] },
  s: ShareholdingInformationPerShareClass,
  language: Language,
): ShareholdingSectionCard => ({
  title: findShareTypeStateName(state, s.shareTypeId, language),
  subtitle: { key: "shareholder-details.number-of-shares" },
  infoPopover: {
    content: {
      key: "shareholder-view.share-information.share-class-vote-info",
      params: {
        shareTypeName: findShareTypeStateName(state, s.shareTypeId, language, { singular: true }),
        count: getShareTypeVote(s.shareTypeId, state.shareTypes),
      },
    },
  },
  value: { type: "number", value: s.numberOfShares },
  extraInfo: {
    key: "x-of-total-xs",
    params: {
      x: { type: "percent", value: s.ownership ?? 0 },
      xs: { type: "number", value: s.totalNumberOfSharesInCompany },
      unit: findShareTypeStateName(state, s.shareTypeId, language),
    },
  },
});

const prepPledgeOverviewCard = (
  state: { shareholder?: ShareholderDetailsResponseDTO },
  actions: { editPledge?: () => void },
): ShareholdingSectionCard => ({
  title: { key: "shareholders.pledged-shares" },
  subtitle: { key: "status" },
  value: state.shareholder?.isPledged
    ? { key: "shareholder-details.shares-are-pledged" }
    : undefined,
  extraInfo: state.shareholder?.isPledged
    ? { translated: state.shareholder?.pledgeComment ?? "" }
    : { key: "shareholders.shares-are-not-pledged" },
  action: actions.editPledge
    ? { label: { key: "edit" }, icon: "edit", onClick: actions.editPledge }
    : undefined,
});

const prepTotalCostPriceCard = (state: {
  shareholder?: ShareholderDetailsResponseDTO;
  currency: string;
}): ShareholdingSectionCard => ({
  title: { key: "shareholder-details.total-cost-price" },
  subtitle: { translated: state.currency },
  infoPopover: { content: { key: "shareholder-details.total-cost-price.help-text" } },
  value:
    state.shareholder?.shareInformation?.totalPaid !== undefined
      ? {
          type: "number",
          value: state.shareholder.shareInformation.totalPaid,
          maximumFractionDigits: 2,
        }
      : "",
  extraInfo: state.shareholder?.shareInformation?.lastTransactionDate
    ? {
        key: "shareholder-details.last-transaction",
        params: {
          date: {
            type: "date",
            value: state.shareholder?.shareInformation?.lastTransactionDate,
          },
        },
      }
    : undefined,
});

const prepLockUpOverviewCard = (state: {
  shareholder?: ShareholderDetailsResponseDTO;
}): ShareholdingSectionCard | undefined =>
  state.shareholder?.lockupEndDate
    ? {
        title: { key: "lock-up" },
        subtitle: { key: "end-date" },
        infoPopover: { content: { key: "shareholder-details.lock-up.help-text" } },
        value: { type: "date", value: state.shareholder.lockupEndDate },
      }
    : undefined;

const prepLoanAmountCard = (state: {
  shareholder?: ShareholderDetailsResponseDTO;
  currency: string;
}): ShareholdingSectionCard | undefined =>
  state.shareholder?.loanAmount
    ? {
        title: { key: "loan-amount" },
        subtitle: { translated: state.currency },
        infoPopover: { content: { key: "shareholder-details.loan-amount.help-text" } },
        value: { type: "number", value: state.shareholder.loanAmount },
      }
    : undefined;

const prepShareholderRemarksCard = (
  state: { shareholder?: ShareholderDetailsResponseDTO },
  actions: { editRemarks?: (remark?: string) => void },
): ShareholdingSectionCard[] | undefined =>
  state.shareholder?.remark || actions.editRemarks
    ? [
        {
          title: { key: "remarks" },
          action: actions.editRemarks
            ? {
                label: { key: "edit" },
                icon: "edit",
                onClick: () => actions.editRemarks?.(state.shareholder?.remark),
              }
            : undefined,
          text: state.shareholder?.remark ? state.shareholder.remark : undefined,
          subtitle:
            state.shareholder?.remark === undefined
              ? {
                  key: "shareholder-details.remarks.emptystate",
                  params: { shareholderName: state.shareholder?.shareholderName },
                }
              : undefined,
          colSpan: "full",
        },
      ]
    : undefined;

export const prepShareholdingOverviewCards = (
  state: { shareholder?: ShareholderDetailsResponseDTO; shareTypes: ShareType[]; currency: string },
  actions: ShareholderDetailsActions,
  language: Language,
): ShareholdingSectionCard[] => {
  const shareInformation = state.shareholder?.shareInformation;
  const shareholding = shareInformation?.shareholding ?? [];
  return compact<ShareholdingSectionCard>([
    prepTotalSharesCard(shareInformation),
    ...shareholding.map<ShareholdingSectionCard>((p) =>
      prepShareClassOverviewCard(state, p, language),
    ),
    prepPledgeOverviewCard(state, actions),
    showCostPrice(state) && prepTotalCostPriceCard(state),
    prepTaxAssessmentOverviewCard(state),
    prepLockUpOverviewCard(state),
    prepLoanAmountCard(state),
  ]);
};

export const prepShareholdingPerClassCharts = (
  s: ShareholdingInformationPerShareClass,
  showCostPrice: boolean,
  shareTypes: ShareType[],
  language: Language,
): ShareholdingSectionCard[][] => {
  const costPricePerShare = (d: ShareIntervalInformation): number | undefined =>
    d.costPricePerShare;
  const formatCostPricePerShare = (d: ShareIntervalInformation): TMessageParam | string =>
    d.costPricePerShare !== undefined
      ? {
          type: "number",
          value: d.costPricePerShare,
          maximumFractionDigits: 2,
        }
      : "";
  const formatSharePremium = (d: ShareIntervalInformation): TMessageParam | string =>
    d.sharePremium !== undefined
      ? {
          type: "number",
          value: d.sharePremium,
          maximumFractionDigits: 2,
        }
      : "";
  const sharePremium = (d: ShareIntervalInformation): number | undefined => d.sharePremium;
  const paidInShareCapitalPerShare = (d: ShareIntervalInformation): number | undefined =>
    d.paidInShareCapitalPerShare;
  const formatPaidInShareCapitalPerShare = (d: ShareIntervalInformation): TMessageParam | string =>
    d.paidInShareCapitalPerShare !== undefined
      ? {
          type: "number",
          value: d.paidInShareCapitalPerShare,
          maximumFractionDigits: 2,
        }
      : "";
  const bonusShareCapitalPerShare = (d: ShareIntervalInformation): number | undefined =>
    d.bonusShareCapitalPerShare;
  const formatBonusShareCapitalPerShare = (d: ShareIntervalInformation): TMessageParam | string =>
    d.bonusShareCapitalPerShare !== undefined
      ? {
          type: "number",
          value: d.bonusShareCapitalPerShare,
          maximumFractionDigits: 2,
        }
      : "";
  const domain = combineExtents(
    compact([
      showCostPrice && extent(s.shareIntervals, costPricePerShare),
      extent(s.shareIntervals, paidInShareCapitalPerShare),
      extent(s.shareIntervals, sharePremium),
      extent(s.shareIntervals, bonusShareCapitalPerShare),
    ]),
  );
  const costPrice =
    showCostPrice &&
    prepShareIntervalKpiCard({
      data: s,
      title: { key: "costPrice" },
      helpText: { key: "shareholder-details.cost-price.help-text" },
      val: costPricePerShare,
      formatVal: formatCostPricePerShare,
      domain,
      shareTypes,
      language,
    });
  const hasPaidInShareCapital = s.shareIntervals.some(
    (d) => d.paidInShareCapitalPerShare !== undefined && d.paidInShareCapitalPerShare !== 0,
  );
  const shareCapital =
    hasPaidInShareCapital &&
    prepShareIntervalKpiCard({
      data: s,
      title: { key: "shareholder-details.paid-in-share-capital" },
      helpText: { key: "shareholder-details.paid-in-share-capital.help-text" },
      val: paidInShareCapitalPerShare,
      formatVal: formatPaidInShareCapitalPerShare,
      domain,
      shareTypes,
      language,
    });
  const premium = prepShareIntervalKpiCard({
    data: s,
    title: { key: "shareholder-details.paid-in-premium" },
    helpText: { key: "shareholder-details.paid-in-premium.help-text" },
    val: sharePremium,
    formatVal: formatSharePremium,
    domain,
    shareTypes,
    language,
  });
  const hasBonusShareCapital = s.shareIntervals.some(
    (d) => d.bonusShareCapitalPerShare !== undefined && d.bonusShareCapitalPerShare !== 0,
  );
  const bonusShareCapital =
    hasBonusShareCapital &&
    prepShareIntervalKpiCard({
      data: s,
      title: { key: "shareholder-details.bonus-share-capital" },
      val: bonusShareCapitalPerShare,
      formatVal: formatBonusShareCapitalPerShare,
      domain,
      shareTypes,
      language,
    });
  if (s.shareIntervals.length >= 10) {
    return compact([
      costPrice && [costPrice],
      shareCapital && [shareCapital],
      [premium],
      bonusShareCapital && [bonusShareCapital],
    ]);
  }
  return [compact([costPrice, shareCapital, premium, bonusShareCapital])];
};

export const prepShareholdingSections = (
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
  language: Language,
): ShareholdingSectionProps[] => {
  const shareholdingPerClass = getShareholdingPerClass(state);
  return compact([
    shareholdingPerClass.length > 1 && {
      title: { key: "shareholder-details.shareholding-overview" },
      subtitle: { key: "all-amounts-in-currency", params: { currency: state.currency } },
      cards: compact([
        prepShareholdingOverviewCards(state, actions, language),
        prepShareholderRemarksCard(state, actions),
      ]),
    },
    ...shareholdingPerClass.map<ShareholdingSectionProps>((s) => ({
      title: findShareTypeStateName(state, s.shareTypeId, language),
      icon: "article",
      cards: compact([
        shareholdingPerClass.length <= 1 && prepShareholdingOverviewCards(state, actions, language),
        [
          {
            title: { key: "shareholder-details.shareholding-details" },
            table: prepShareIntervalsTable(s, showCostPrice(state)),
          },
        ],
        ...prepShareholdingPerClassCharts(s, showCostPrice(state), state.shareTypes, language),
        prepFairValueCards(
          state.shareTypes.find((st) => st.sharetypeId === s.shareTypeId),
          state.shareholder?.fairValueInformation,
        ),
        shareholdingPerClass.length <= 1 && prepShareholderRemarksCard(state, actions),
      ]),
    })),
  ]);
};

export const prepShareholdingEmptyState = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): EmptyStateProps | undefined => {
  const shareInfo = state.shareholder?.shareInformation;
  if (isFormerShareholder(state)) {
    return {
      illustration: "file-x",
      maxWidth: "xl",
      title: {
        key: "shareholder-details.former-shareholder.title",
        params: { date: { type: "date", value: shareInfo?.lastTransactionDate } },
      },
      description:
        shareInfo?.firstTransactionDate && shareInfo.lastTransactionDate
          ? {
              key: "shareholder-details.former-shareholder.description",
              params: {
                fromDate: {
                  type: "date",
                  value: shareInfo.firstTransactionDate,
                },
                toDate: { type: "date", value: shareInfo.lastTransactionDate },
                name: state.shareholder?.shareholderName,
                company: context.companyName,
              },
            }
          : undefined,
      actions: [
        {
          type: "button",
          key: "open-transactions",
          props: {
            variant: "secondary",
            label: {
              key: "shareholder-details.former-shareholder.open-transactions",
              params: { count: state.shareholder?.transactionLog?.length ?? 0 },
            },
            onClick: () => actions.openTab?.("transactions"),
          },
        },
      ],
    };
  } else if (isNotShareholder(state)) {
    return {
      illustration: "file-x",
      maxWidth: "xl",
      title: { key: "shareholder-details.not-a-shareholder.title" },
      description: { key: "shareholder-details.not-a-shareholder.description" },
      actions: compact([
        actions.deleteShareholder && {
          type: "link-button",
          key: "delete",
          props: {
            label: {
              key: "shareholder-details.not-a-shareholder.delete.label",
              params: {
                shareholder: state.shareholder?.shareholderName,
                company: context.companyName,
              },
            },
            icon: "trash",
            color: "error",
            onClick: () =>
              actions.deleteShareholder?.({
                key: "shareholder-details.not-a-shareholder.delete.confirm",
                params: {
                  shareholder: state.shareholder?.shareholderName,
                },
              }),
            loading: state.isDeleting,
          },
        },
      ]),
    };
  }
};

export const prepShareholdingAlert = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
): AlertProps | undefined =>
  when(state.shareholder?.hasIncompleteCostPriceHistory, () => ({
    type: "info",
    icon: "info",
    message: {
      key: "shareholder-details.cost-price.info",
      params: {
        msg: {
          key: state.shareholder?.overrideCostPriceDisplay
            ? "settings.override-cost-price-display.enabled"
            : "settings.override-cost-price-display.disabled",
        },
        settingsUrl: adminSettingsShareholders(context.orgNo),
      },
    },
  }));

export const prepTransactionsSummaryInfo = (
  state: ShareholderDetailsState,
): AlertProps | undefined =>
  state.shareholder?.transactionSummary?.hasFullHistory ||
  !state.shareholder?.transactionSummary?.firstRealTransactionDate
    ? undefined
    : {
        icon: "info",
        message: {
          key: "shareholder-details.incomplete-transaction-log-info",
          params: {
            firstRealTransactionDate: {
              type: "date",
              value: state.shareholder.transactionSummary.firstRealTransactionDate,
            },
          },
        },
        type: "info",
      };

export const prepTransactionSummaryCards = (
  state: ShareholderDetailsState,
): DataCardProps[] | undefined => {
  const cards: [title: TMessageKey, helpText: TMessageKey, SummaryValue?][] = [
    [
      "acquisitions",
      "shareholder-details.acquisitions.help-text",
      state.shareholder?.transactionSummary?.acquisitions,
    ],
    ["sales", "shareholder-details.sales.help-text", state.shareholder?.transactionSummary?.sales],
    [
      "dividends",
      "shareholder-details.dividends.help-text",
      state.shareholder?.transactionSummary?.dividends,
    ],
  ];
  if (cards.every(([, , val]) => !val?.total)) {
    return;
  }
  return cards.map(([title, helpText, val]) => ({
    title: { key: title },
    subtitle: { key: "total-currency", params: { currency: state.currency } },
    infoPopover: { content: { key: helpText } },
    value: val ? { type: "amount", value: val?.total } : undefined,
    extraInfo: val?.avg
      ? {
          key: "shareholder-details.avg-per-share",
          params: { value: { type: "exactAmount", value: val.avg, currency: state.currency } },
        }
      : undefined,
  }));
};

export const prepTransactionsTable = (
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): DataTableProps<Transaction> => {
  return {
    tableData: state.shareholder?.transactionLog ?? [],
    tableColumns: prepareTransactionLogTableColumns({
      hideUpdated: true,
    }),
    hoverable: true,
    withRowBorders: true,
    rowActions: actions.downloadContractNote
      ? (row) =>
          prepareTransactionLogTableRowActions(row, {
            downloadContractNote: actions.downloadContractNote,
          })
      : undefined,
    getId: (row) => row.transactionlogId,
    onRowClick: actions.openTransaction
      ? (row) => actions.openTransaction?.(row.transactionlogId)
      : undefined,
  };
};

export const userAccountCard = (
  state: {
    canLogOnWithPassword?: boolean;
    canLogOnWithBankId?: boolean;
    shareholder?: ShareholderDetailsResponseDTO;
  },
  actions: { sendInvite?: () => void },
  lastLogin?: string,
): DetailsCardProps => ({
  title: { key: "user-account" },
  fields: compact([
    [
      "shareholders.detailed.loginWith",
      compact([
        state.canLogOnWithPassword && { key: "login-method-password" },
        state.canLogOnWithBankId && { key: "common.bankid" },
        !state.canLogOnWithPassword &&
          !state.canLogOnWithBankId && { key: "no-login-method-available" },
      ]),
    ],
    state.canLogOnWithPassword && [
      "user-settings.username",
      state.shareholder?.usernames.filter((d) => d.hasPassword).map((d) => d.username),
    ],
    [
      "last-login",
      lastLogin
        ? { type: "datetime", value: lastLogin, relative: true }
        : { key: "never-logged-in" },
      actions.sendInvite
        ? { label: { key: "shareholders.detailed.send-login-link" }, onClick: actions.sendInvite }
        : undefined,
    ],
  ]),
});

export const bankDetailsCard = (
  s: ShareholderDetailsResponseDTO,
  actions: { edit?: () => void },
): DetailsCardProps => ({
  title: { key: "bank-details" },
  action: actions.edit
    ? { label: { key: "edit" }, icon: "edit", onClick: actions.edit }
    : undefined,
  fields: [
    { heading: { key: "shareholders.editSingle.account" } },
    ["shareholders.editSingle.bankDetailsCountry", countryOrText(s.bankDetailsCountry)],
    ["shareholders.editSingle.iban", s.iban],
    [
      "shareholders.editSingle.bankAccountName",
      s.bankAccountName,
      { info: { key: "shareholders.editSingle.bankAccountName-info" } },
    ],
    [
      "shareholders.editSingle.bankPaymentReference",
      s.bankPaymentReference,
      { info: { key: "shareholders.editSingle.bankPaymentReference-info" } },
    ],
    { heading: { key: "shareholders.editSingle.bank" } },
    ["shareholders.editSingle.bic", s.bic],
    ["shareholders.editSingle.bankName", s.bankName],
    [
      "shareholders.editSingle.bankRoutingCode",
      s.bankRoutingCode,
      { info: { key: "shareholders.editSingle.bankRoutingCode-info" } },
    ],
    ["shareholders.editSingle.bankAddressLine1", s.bankAddressLine1],
    ["shareholders.editSingle.bankAddressLine2", s.bankAddressLine2],
    ["shareholders.editSingle.postalCode", s.bankPostalCode],
    ["shareholders.editSingle.city", s.bankCity],
    ["shareholders.editSingle.bankState", s.bankState],
    ["shareholders.editSingle.country", countryOrText(s.bankCountry)],
  ],
});

const countryOrText = (value?: string): TMessageParam | string | undefined =>
  value && isCountryCode(value) ? { type: "country", code: value } : value;

export const additionDetailsCard = (s: ShareholderDetailsResponseDTO): DetailsCardProps => ({
  title: { key: "additional-details" },
  fields: [
    ["shareholders.detailed.gender", when(s.personInfo?.gender, getGenderLabel)],
    [
      "shareholders.detailed.isEmployee",
      s.isEmployee === undefined ? undefined : s.isEmployee ? { key: "yes" } : { key: "no" },
    ],
  ],
});

export const personalShareholderCard = (
  s: ShareholderDetailsResponseDTO,
  isSwedishOrgNo: boolean,
  isHolder = false,
): DetailsCardProps => ({
  title: {
    key: isHolder
      ? "shareholder-details.personal-holder"
      : "shareholder-details.personal-shareholder",
  },
  fields: compact([
    ["name", getFullName(s.personInfo)],
    ["shareholders.editSingle.socialSecurityNumber", s.personInfo?.socialSecurityNumber],
    [
      "shareholders.detailed.nationality",
      s.nationality ? { type: "country", code: s.nationality } : undefined,
    ],
    isSwedishOrgNo && [
      "shareholders.detailed.issuedShareholderCertificate",
      s.issuedShareholderCertificate ? { key: "yes" } : { key: "no" },
    ],
    [
      "shareholders.detailed.birth-date",
      s.personInfo?.birthDate ? { type: "date", value: s.personInfo?.birthDate } : undefined,
    ],
    ["shareholders.editSingle.utlNumber", s.utlNumber],
    ["email", s.personInfo?.email],
    ["settings.phoneNumber", formatPhoneNumber(s.personInfo?.phoneNumber)],
    ["address", s.shareholderAddress?.address],
    ["shareholders.editSingle.postalCode", s.shareholderAddress?.postalCode],
    ["newShareholder.city", s.shareholderAddress?.city],
    [
      "newShareholder.country",
      s.shareholderAddress?.country
        ? { type: "country", code: s.shareholderAddress?.country }
        : undefined,
    ],
  ]),
});

export const prepPersonalShareholderCards = (
  context: { orgNo: string; isShareholderView?: boolean },
  state: {
    canLogOnWithPassword?: boolean;
    canLogOnWithBankId?: boolean;
    shareholder?: ShareholderDetailsResponseDTO;
  },
  actions: ShareholderDetailsActions,
  isHolder = false,
): (DetailsCardProps | ShareholdingSectionCard)[] => {
  const s = state.shareholder;
  return !s
    ? []
    : compact([
        personalShareholderCard(s, isSwedishOrgNo(context.orgNo), isHolder),
        !context.isShareholderView && userAccountCard(state, actions, s.lastLogin),
        bankDetailsCard(s, actions),
        additionDetailsCard(s),
        ...(prepShareholderRemarksCard(state, actions) ?? []),
      ]);
};

export const primaryRepresentativeCard = (
  s: ShareholderDetailsResponseDTO,
  isSwedishOrgNo: boolean,
): DetailsCardProps => ({
  title: { key: "shareholder-details.primary-representative" },
  fields: compact([
    ["name", getFullName(s.contactInfo)],
    ["shareholders.editSingle.socialSecurityNumber", s.contactInfo?.socialSecurityNumber],

    isSwedishOrgNo && [
      "shareholders.detailed.issuedShareholderCertificate",
      s.issuedShareholderCertificate ? { key: "yes" } : { key: "no" },
    ],
    [
      "shareholders.detailed.birth-date",
      when(s.contactInfo?.birthDate, (value) => ({ type: "date", value })),
    ],
    ["email", s.contactInfo?.email],
    ["settings.phoneNumber", formatPhoneNumber(s.contactInfo?.phoneNumber)],
    ["shareholders.detailed.gender", when(s.contactInfo?.gender, getGenderLabel)],
    [
      "shareholders.detailed.isEmployee",
      s.isEmployee === undefined ? undefined : s.isEmployee ? { key: "yes" } : { key: "no" },
    ],
  ]),
});

export const companyShareholderCard = (
  s: ShareholderDetailsResponseDTO,
  isHolder = false,
): DetailsCardProps => ({
  title: {
    key: isHolder
      ? "shareholder-details.company-holder"
      : "shareholder-details.company-shareholder",
  },
  fields: [
    [
      "shareholders.detailed.nationality",
      s.nationality ? { type: "country", code: s.nationality } : undefined,
    ],
    ["org-no", formatOrgNum(s.companyNumber)],
    ["shareholders.editSingle.utlNumber", s.utlNumber],
    ["shareholders.company-name", s.companyName],
    ["address", s.shareholderAddress?.address],
    ["shareholders.editSingle.postalCode", s.shareholderAddress?.postalCode],
    ["newShareholder.city", s.shareholderAddress?.city],
    [
      "newShareholder.country",
      s.shareholderAddress?.country
        ? { type: "country", code: s.shareholderAddress?.country }
        : undefined,
    ],
  ],
});

const prepCompanyShareholderCards = (
  context: { orgNo: string; isShareholderView?: boolean },
  state: {
    canLogOnWithPassword?: boolean;
    canLogOnWithBankId?: boolean;
    shareholder?: ShareholderDetailsResponseDTO;
  },
  actions: ShareholderDetailsActions,
  isHolder = false,
): (DetailsCardProps | ShareholdingSectionCard)[] => {
  const s = state.shareholder;
  return !s
    ? []
    : compact([
        companyShareholderCard(s, isHolder),
        primaryRepresentativeCard(s, isSwedishOrgNo(context.orgNo)),
        !context.isShareholderView && userAccountCard(state, actions, s.lastLogin),
        bankDetailsCard(s, actions),
        ...(prepShareholderRemarksCard(state, actions) ?? []),
      ]);
};

export const prepDetailsCards = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): (DetailsCardProps | ShareholdingSectionCard)[] => {
  const s = state.shareholder;
  const isHolder = isOptionHolderWithoutShares(context, state);
  return !s
    ? []
    : s.isCompany
      ? prepCompanyShareholderCards(context, state, actions, isHolder)
      : prepPersonalShareholderCards(context, state, actions, isHolder);
};

export const prepAdditionalRepresentativesTable = (
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): DataTableProps<PersonInfo> => {
  return {
    getId: (row) => String(row.personInfoId),
    withRowBorders: true,
    tableColumns: [
      {
        name: "name",
        align: "left",
        label: { key: "name" },
        getValue: getFullName,
        getSortValue: getFullName,
      },
      {
        name: "birthday",
        label: { key: "shareholders.detailed.birth-date" },
        getValue: (row) => (row.birthDate ? { type: "date", value: row.birthDate } : undefined),
        getSortValue: (row) => row.birthDate,
      },
      {
        name: "last-login",
        label: { key: "last-login" },
        getValue: (row) =>
          row.lastLogin ? { type: "datetime", value: row.lastLogin, relative: true } : undefined,
        getSortValue: (row) => row.lastLogin,
      },
    ],
    tableData: state.shareholder?.additionalRepresentatives ?? [],
    rowActions: (row) =>
      compact(
        actions.deleteRepresentative && [
          [
            "icon",
            {
              icon: "trash",
              tooltip: { key: "delete" },
              color: "error",
              onClick: () => actions.deleteRepresentative?.(row.personInfoId!),
            },
          ],
        ],
      ),
  };
};

export const prepShareholderDetailsActions = ({
  activeTab,
  dispatch,
  lang,
  orgNo,
  setShareholderStatusFilter,
  shareholderId,
  referrerUrl,
  shareholderUrl,
  shareholderDetailsStore,
  commitmentCtrl,
  currentUrl,
  canEdit,
  transactionDetailsUrl,
  commitmentsUrl,
  commitmentDetailsUrl,
  contributionCtrl,
  contributionsUrl,
  contributionDetailsUrl,
  editDetails,
  deleteRepresentative,
  deleteShareholder,
  downloadShareholderCertificate,
  messageStore,
  apiService,
  http,
  user,
  companyName,
}: {
  activeTab?: ShareholderDetailsTab;
  dispatch: EventDispatcher;
  lang: Language;
  orgNo: string;
  setShareholderStatusFilter?: (s: ShareholderStatus) => void;
  shareholderId: number;
  referrerUrl?: string;
  shareholderUrl: (orgNo: string, shareholderId: number, tab?: ShareholderDetailsTab) => string;
  shareholderDetailsStore: ShareholderDetailsStore;
  commitmentCtrl: ShareholderCommitmentController;
  contributionCtrl: ShareholderContributionController;
  currentUrl: string;
  canEdit: boolean;
  transactionDetailsUrl?: (orgNo: string, transactionId: number) => string;
  commitmentsUrl?: (orgNo: string) => string;
  commitmentDetailsUrl?: (orgNo: string, id: string) => string;
  contributionsUrl?: (orgNo: string) => string;
  contributionDetailsUrl?: (orgNo: string, id: number) => string;
  editDetails?: () => void;
  deleteShareholder: () => void;
  deleteRepresentative: (id: number) => void;
  downloadShareholderCertificate: () => void;
  messageStore: AlertMessageStore;
  apiService: ApiService;
  http: AxiosInstance;
  user: User | null;
  companyName?: string;
}): ShareholderDetailsActions => {
  const close = referrerUrl ? () => dispatch([{ type: "navigate", url: referrerUrl }]) : undefined;
  return {
    edit: canEdit ? editDetails : undefined,
    downloadShareholderCertificate:
      !isSwedishOrgNo(orgNo) &&
      shareholderDetailsStore.shareholderDetails &&
      shareholderDetailsStore.shareholderDetails.shareInformation.totalShares > 0
        ? downloadShareholderCertificate
        : undefined,
    sendInvite:
      canEdit &&
      shareholderDetailsStore.shareholderDetails &&
      (shareholderDetailsStore.shareholderDetails.shareInformation.totalShares > 0 ||
        shareholderDetailsStore.shareholderDetails.commitments.length > 0 ||
        shareholderDetailsStore.shareholderDetails.isOptionHolder === true)
        ? () =>
            dispatch([
              {
                type: "show-dialog",
                dialog: {
                  type: "sendInvitation",
                  model: new SendInvitationDialogStore(
                    {
                      invitationType: InvitationType.SHAREHOLDER,
                      shareholderId: shareholderId,
                      organisationNumber: orgNo,
                      lang: lang,
                      title: {
                        key: "shareholders.invite.title",
                        params: { name: shareholderDetailsStore.shareholderOrCompanyName },
                      },
                      description: { key: "shareholders.notify.description" },
                      email: shareholderDetailsStore.personOrContactInfo?.email,
                      isShareholderPortalEnabled:
                        shareholderDetailsStore.shareholderDetails?.isShareholderPortalEnabled ??
                        true,
                    },
                    apiService,
                    messageStore,
                    () => dispatch([{ type: "closeDialog" }]),
                    new AIClient(http, orgNo),
                    {
                      key: "invite-user.email.default-text",
                      params: {
                        senderName: getFullName(user),
                        email: user?.email,
                        companyName,
                      },
                    },
                  ),
                },
              },
            ])
        : undefined,
    editPledge: canEdit
      ? () => shareholderDetailsStore.shareholderPledgeForm.setMode("edit")
      : undefined,
    editRemarks: canEdit
      ? (remark) => shareholderDetailsStore.shareholderRemarksForm.openDialog(remark)
      : undefined,
    openTab: (tab) =>
      dispatch([
        {
          type: "navigate",
          url: shareholderUrl(orgNo, shareholderId, tab),
          state: referrerUrl === undefined ? undefined : navigationState({ referrer: referrerUrl }),
        },
      ]),
    openTransaction: transactionDetailsUrl
      ? (id: number) =>
          dispatch([
            {
              type: "load-url",
              url: transactionDetailsUrl(orgNo, id),
              newWindow: true,
            },
          ])
      : undefined,
    openInviteRepresentativeDialog:
      canEdit &&
      shareholderDetailsStore.shareholderDetails &&
      (shareholderDetailsStore.shareholderDetails.shareInformation.totalShares > 0 ||
        shareholderDetailsStore.shareholderDetails.commitments.length > 0)
        ? () =>
            dispatch([
              {
                type: "show-dialog",
                dialog: {
                  type: "sendInvitation",
                  model: new SendInvitationDialogStore(
                    {
                      invitationType: InvitationType.SHAREHOLDER_REPRESENTATIVE,
                      shareholderId: shareholderId,
                      organisationNumber: orgNo,
                      lang: lang,
                      title: { key: "shareholders.invite-representative.title" },
                      isShareholderPortalEnabled:
                        shareholderDetailsStore.shareholderDetails?.isShareholderPortalEnabled ??
                        true,
                    },
                    apiService,
                    messageStore,
                    () => dispatch([{ type: "closeDialog" }]),
                    new AIClient(http, orgNo),
                    {
                      key: "invite-user.email.default-text-additional-representative",
                      params: {
                        senderName: getFullName(user),
                        email: user?.email,
                        userName: shareholderDetailsStore.shareholderOrCompanyName,
                        companyName,
                      },
                    },
                  ),
                },
              },
            ])
        : undefined,
    deleteRepresentative: canEdit ? deleteRepresentative : undefined,
    selectShareholder: (s) =>
      dispatch([
        {
          type: "navigate",
          url: shareholderUrl(orgNo, s.id, activeTab),
          state: referrerUrl === undefined ? undefined : navigationState({ referrer: referrerUrl }),
        },
      ]),
    close,
    setShareholderStatusFilter: setShareholderStatusFilter,
    navigateToCommitments: commitmentsUrl
      ? () => dispatch([{ type: "navigate", url: commitmentsUrl(orgNo) }])
      : undefined,
    navigateToCommitmentDetails: commitmentDetailsUrl
      ? (id) =>
          dispatch([
            {
              type: "navigate",
              url: commitmentDetailsUrl(orgNo, id),
              state: navigationState({ referrer: currentUrl }),
            },
          ])
      : undefined,
    changeCommitmentDate: canEdit
      ? (id) => commitmentCtrl.showChangeDateDialogForCommitment(id)
      : undefined,
    deleteCommitment: canEdit
      ? (id) => commitmentCtrl.showDeleteConfirmationDialogForCommitment(id)
      : undefined,
    deleteShareholder: canEdit
      ? (confirmText: TMessage) => {
          dispatch(
            confirmAction({
              text: confirmText,
              onConfirm: [thunk(() => deleteShareholder())],
            }),
          );
        }
      : undefined,
    navigateToContributions: contributionsUrl
      ? () =>
          dispatch([
            navigate(contributionsUrl(orgNo), {
              state: navigationState({ referrer: currentUrl }),
            }),
          ])
      : undefined,
    navigateToContributionDetails: contributionDetailsUrl
      ? (id) =>
          dispatch([
            navigate(contributionDetailsUrl(orgNo, id), {
              state: navigationState({ referrer: currentUrl }),
            }),
          ])
      : undefined,
    showChangeDateDialogForContribution: canEdit
      ? contributionCtrl.showChangeDateDialogForContribution
      : undefined,
    showDeleteConfirmationDialogForContribution: canEdit
      ? contributionCtrl.showDeleteConfirmationDialogForContribution
      : undefined,
  };
};

export type ShareDetailsDialogsProps = {
  editPledge: {
    form: ShareholderPledgeForm;
    onSubmit: () => void;
    onCancel: () => void;
  };
  editRemarks: {
    form: ShareholderRemarksForm;
    onSubmit: () => void;
    onCancel: () => void;
  };
  commitmentCtrl: ShareholderCommitmentController;
  contributionCtrl: ShareholderContributionController;
};

export const prepShareholderDetailsDialogs = (
  shareholdersStore: ShareholdersStore,
  commitmentCtrl: ShareholderCommitmentController,
  contributionCtrl: ShareholderContributionController,
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
): ShareDetailsDialogsProps => {
  const pledgeForm = shareholdersStore.shareholderDetailsStore.shareholderPledgeForm;
  const remarksForm = shareholdersStore.shareholderDetailsStore.shareholderRemarksForm;
  return {
    editPledge: {
      form: pledgeForm,
      onCancel: () => pledgeForm.setMode("view"),
      onSubmit: () => {
        flowResult(
          shareholdersStore.updateShareholderPledge(context.shareholderId, pledgeForm),
        ).then(
          action(() => {
            if (state.shareholder) {
              state.shareholder.isPledged = pledgeForm.isPledged.value ?? false;
              state.shareholder.pledgeComment = pledgeForm.comment.validatedValue;
            }
          }),
        );
      },
    },
    editRemarks: {
      form: remarksForm,
      onCancel: () => remarksForm.setMode("hide"),
      onSubmit: () => {
        remarksForm.validate();
        if (remarksForm.isValid) {
          flowResult(
            shareholdersStore.updateShareholderRemarks(context.shareholderId, remarksForm),
          ).then(
            action(() => {
              if (state.shareholder) {
                state.shareholder.remark = remarksForm.remarkValue;
              }
            }),
          );
        }
      },
    },
    commitmentCtrl,
    contributionCtrl,
  };
};

export const prepCommitmentsData = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): ShareholderCommitmentsViewProps => {
  const commitments = (isOk(context.commitments) ? context.commitments.data : []).filter((c) =>
    c.details.some((d) => d.shareholderId === context.shareholderId),
  );
  const data = prepareCommitmentOverviewRows(
    commitments,
    state.filteredShareholders,
    totalNumberOfSharesOfShareholders(state.filteredShareholders),
  );
  return {
    total: prepareCommitmentOverviewTotal(commitments, data, state.currency),
    table: prepareShareholderCommitmentsTable({
      data: prepareCommitmentRows(commitments, state.filteredShareholders, context.t),
      canEdit: Boolean(context.canEdit),
      navigateToDetails: actions.navigateToCommitmentDetails,
      deleteCommitment: actions.deleteCommitment,
      changeDate: actions.changeCommitmentDate,
    }),
    emptyState: isEmpty(commitments)
      ? prepareShareholderCommitmentsEmptyState({
          action: actions.navigateToCommitments
            ? {
                variant: "secondary",
                arrow: "out",
                label: { key: "shareholder-commitments.manage-commitments" },
                onClick: actions.navigateToCommitments,
              }
            : undefined,
        })
      : undefined,
  };
};

export const prepContributionsView = (
  context: ShareholderDetailsContext,
  state: ShareholderDetailsState,
  actions: ShareholderDetailsActions,
): ShareholderContributionsViewProps => {
  const contributions = (isOk(context.contributions) ? context.contributions.data : []).filter(
    (c) => c.details.some((d) => d.shareholderId === context.shareholderId),
  );
  const data = prepareOverviewRows(
    contributions,
    state.filteredShareholders,
    totalNumberOfSharesOfShareholders(state.filteredShareholders),
  );
  const total = prepareContributionOverviewTotal(contributions, data);
  return {
    total: [
      [
        "committed",
        {
          title: { key: "shareholder-contributions.committed" },
          subtitle: { translated: state.currency },
          value: { type: "number", value: total.committed, maximumFractionDigits: 2 },
          extraInfo: total.lastRegistrationDate
            ? {
                key: "shareholder-contributions.total.committed.extra-text",
                params: { date: { type: "date", value: total.lastRegistrationDate } },
              }
            : undefined,
          infoPopover: { content: { key: "shareholder-contributions.total.committed.help-text" } },
        },
      ],
      [
        "paid-in",
        {
          title: { key: "shareholder-contributions.paid-in" },
          subtitle: { translated: state.currency },
          value: { type: "number", value: total.contributed, maximumFractionDigits: 2 },
          extraInfo: {
            key: "shareholder-contributions.total.paid-in.extra-text",
            params: { amount: { type: "number", value: total.net, maximumFractionDigits: 2 } },
          },
          infoPopover: { content: { key: "shareholder-contributions.total.paid-in.help-text" } },
        },
      ],
      [
        "distributed",
        {
          title: { key: "shareholder-contributions.distributed" },
          subtitle: { translated: state.currency },
          value: { type: "number", value: total.distributed, maximumFractionDigits: 2 },
          extraInfo: {
            key: "shareholder-contributions.total.distributed.extra-text",
            params: {
              amount: { type: "number", value: total.recallable, maximumFractionDigits: 2 },
            },
          },
          infoPopover: {
            content: { key: "shareholder-contributions.total.distributed.help-text" },
          },
        },
      ],
      [
        "remaining",
        {
          title: { key: "shareholder-contributions.remaining" },
          subtitle: { translated: state.currency },
          value: { type: "number", value: total.remaining, maximumFractionDigits: 2 },
          extraInfo: {
            key: "shareholder-contributions.total.remaining.extra-text",
            params: { percent: { type: "percent", value: total.remainingPercent } },
          },
          infoPopover: {
            content: {
              key: "shareholder-contributions.total.remaining.help-text",
            },
          },
        },
      ],
    ],
    table: prepareContributionsTable({
      data: prepareContributionRows(
        context.lang,
        contributions,
        state.filteredShareholders,
        context.t,
      ),
      navigateToContributionDetails: actions.navigateToContributionDetails,
      showChangeDateDialogForContribution: actions.showChangeDateDialogForContribution,
      showDeleteConfirmationDialogForContribution: actions.showChangeDateDialogForContribution,
    }),
    emptyState: isEmpty(contributions)
      ? {
          ...prepareContributionsEmptyState({ orgNo: context.orgNo }),
          actions: context.canEdit
            ? [
                {
                  type: "button",
                  key: "manage-contributions",
                  props: {
                    variant: "secondary",
                    label: {
                      key: "shareholder-contributions.manage-contributions",
                    },
                    onClick: actions.navigateToContributions,
                  },
                },
              ]
            : undefined,
        }
      : undefined,
  };
};

function findShareTypeStateName(
  state: { shareTypes: ShareType[] },
  shareTypeId: number,
  language: Language,
  options?: { singular: true },
): TMessage {
  return { translated: lookupShareTypeName(shareTypeId, state.shareTypes, language, options) };
}
